// ─── Section 105 Plan Advisor Wizard ───────────────────────────────
// Shared wizard UI used by both Internal (CRM drawer) and Employer (portal dashboard) modes.
// Same decision logic; terminology and CTAs differ by mode.
import React, { useMemo, useState, useEffect } from 'react';
import { C } from '../../utils/styles';
import Icon from '../Icon';
import { useConfig } from '../../context/ConfigContext';
import { useClients } from '../../context/ClientsContext';
import {
  getRecommendedPlanId,
  getPlanTypeById,
  budgetToMonthlyAmount,
  computeEligibilityBridge,
  prefillAnswersFromClient,
  renderDocumentTemplate,
  buildMergeFields,
  getPackageRecommendations,
  getPackageById,
  getPackageDeliverables,
  computePackageCost,
} from '../../utils/section105Advisor';
import { optimizeEmployeeWithEligibility } from '../../utils/calculations';
import { buildSavedOptions } from '../../utils/savedOptionsBuilder';

const MODE_COPY = {
  internal: {
    title: 'Section 105 Plan Advisor',
    subtitle: 'Determine the right HRA structure for this employer',
    sendLabel: 'Send to Employer Portal',
    viewDocsLabel: 'View plan documents',
    restartLabel: 'Restart wizard',
    saveRecLabel: 'Save recommendation',
    closeLabel: 'Close',
  },
  employer: {
    title: 'Healthcare Reimbursement Advisor',
    subtitle: 'Find the right plan for your team in under 2 minutes',
    sendLabel: 'Request consultation',
    viewDocsLabel: 'View sample documents',
    restartLabel: 'Start over',
    saveRecLabel: 'Save my answers',
    closeLabel: 'Close',
  },
};

// Helper to sum annual employer FICA savings from analyzed employees (used for Combined impact panel)
function sumCurrentFica(analyzed) {
  return (analyzed || []).reduce(
    (acc, a) => acc + (a.hasViable && a.optimal ? (a.optimal.employerAnnualFica || 0) : 0),
    0
  );
}

export default function Section105Wizard({ clientId, mode = 'internal', onClose }) {
  const copy = MODE_COPY[mode] || MODE_COPY.internal;
  const isEmployer = mode === 'employer';
  const { config, updateConfig } = useConfig(); // eslint-disable-line no-unused-vars
  const { clients, saveClient } = useClients();

  const client = clients.find((c) => c.id === clientId);
  const section105Config = config?.section105Config || {};
  const questions = section105Config.questions || [];
  const planTypes = section105Config.planTypes || [];
  const documentTemplates = section105Config.documentTemplates || {};
  const packagesList = section105Config.packages || [];
  const coverageLayersMap = section105Config.coverageLayers || {};

  // Stored wizard state lives under client.ibosConfig.section105
  const stored = (client?.ibosConfig && client.ibosConfig.section105) || null;

  const [step, setStep] = useState(() => (stored && stored.answers ? 'results' : 'intro'));
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState(() => {
    if (stored && stored.answers) return stored.answers;
    return prefillAnswersFromClient(client);
  });
  const [viewingDocKey, setViewingDocKey] = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  // Active package selection — defaults to the recommendation's primary
  const [activePackageId, setActivePackageId] = useState(
    () => (stored && stored.selectedPackageId) || null
  );

  // Auto-clear "saved" toast
  useEffect(() => {
    if (!justSaved) return;
    const id = setTimeout(() => setJustSaved(false), 2500);
    return () => clearTimeout(id);
  }, [justSaved]);

  const totalQs = questions.length;
  const q = questions[currentQ];

  // Package recommendation — replaces the legacy single-plan recommendation.
  // Falls back to the legacy plan recommendation if package list is empty.
  const recommendation = useMemo(() => {
    if (!answers) return null;
    const monthly = budgetToMonthlyAmount(answers.budget);
    const recIds = getPackageRecommendations(answers);
    const primaryPkg = getPackageById(recIds.primary, packagesList, planTypes, coverageLayersMap);
    const altPkgs = (recIds.alternatives || [])
      .map(id => getPackageById(id, packagesList, planTypes, coverageLayersMap))
      .filter(Boolean);
    // Legacy fields preserved for backwards-compat with persisted records
    const planId = getRecommendedPlanId(answers);
    const plan = getPlanTypeById(planId, planTypes);
    return {
      planId,
      plan,
      monthlyContribution: monthly,
      primaryPackage: primaryPkg,
      alternativePackages: altPkgs,
    };
  }, [answers, planTypes, packagesList, coverageLayersMap]);

  // Currently displayed package (user can flip between primary and alternatives)
  const activePackage = useMemo(() => {
    if (!recommendation) return null;
    if (activePackageId) {
      return (
        getPackageById(activePackageId, packagesList, planTypes, coverageLayersMap) ||
        recommendation.primaryPackage
      );
    }
    return recommendation.primaryPackage;
  }, [activePackageId, recommendation, packagesList, planTypes, coverageLayersMap]);

  const bridge = useMemo(() => {
    if (!recommendation || !client || !client.employees || client.employees.length === 0) return null;
    const engineConfig = config;
    const analyzed = client.employees.map((e) => {
      const salaryNum = Number(e.salary) || Number(e.annualSalary) || 0;
      if (!salaryNum) return e;
      return optimizeEmployeeWithEligibility({ ...e, salary: salaryNum }, engineConfig);
    });
    return computeEligibilityBridge({
      employees: client.employees,
      analyzed,
      config: engineConfig,
      monthlyContribution: recommendation.monthlyContribution,
    });
  }, [recommendation, client, config]);

  const currentFicaSavings = useMemo(() => {
    if (!client || !client.employees || client.employees.length === 0) return 0;
    const analyzed = client.employees.map((e) => {
      const salaryNum = Number(e.salary) || Number(e.annualSalary) || 0;
      if (!salaryNum) return e;
      return optimizeEmployeeWithEligibility({ ...e, salary: salaryNum }, config);
    });
    return sumCurrentFica(analyzed);
  }, [client, config]);

  const persistAnswers = async (nextAnswers, extraFlags = {}) => {
    if (!client) return;
    const existingIbos = client.ibosConfig || {};
    const existing105 = existingIbos.section105 || {};
    const next105 = {
      ...existing105,
      answers: nextAnswers,
      lastUpdated: new Date().toISOString(),
      mode,
      ...extraFlags,
    };
    await saveClient({
      ...client,
      ibosConfig: { ...existingIbos, section105: next105 },
    });
  };

  const handleAnswer = (questionId, optionId) => {
    const next = { ...answers, [questionId]: optionId };
    setAnswers(next);
    if (currentQ < totalQs - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      // Last question → results
      setStep('results');
      persistAnswers(next);
    }
  };

  const startWizard = () => {
    setStep('wizard');
    setCurrentQ(0);
  };

  const restart = () => {
    setAnswers({});
    setCurrentQ(0);
    setStep('wizard');
  };

  const sendToEmployer = async () => {
    if (!client) return;
    const existingIbos = client.ibosConfig || {};
    const existing105 = existingIbos.section105 || {};
    const selectedPkgId = activePackage?.id || recommendation?.primaryPackage?.id || null;
    const builtOptions = buildSavedOptions({
      client,
      config,
      packageId: selectedPkgId,
      monthlyContribution: recommendation?.monthlyContribution,
      answers,
      createdBy: 'account_manager',
      createdById: null,
    });
    await saveClient({
      ...client,
      ibosConfig: {
        ...existingIbos,
        section105: {
          ...existing105,
          answers,
          presentedToEmployer: true,
          presentedAt: new Date().toISOString(),
          recommendedPlanId: recommendation?.planId,
          monthlyContribution: recommendation?.monthlyContribution,
          recommendedPackageId: recommendation?.primaryPackage?.id,
          alternativePackageIds: (recommendation?.alternativePackages || []).map(p => p.id),
          selectedPackageId: selectedPkgId,
        },
      },
      savedOptions: builtOptions,
    });
    setJustSaved(true);
  };

  const requestConsultation = async () => {
    if (!client) return;
    const existingIbos = client.ibosConfig || {};
    const existing105 = existingIbos.section105 || {};
    const selectedPkgId = activePackage?.id || recommendation?.primaryPackage?.id || null;
    const builtOptions = buildSavedOptions({
      client,
      config,
      packageId: selectedPkgId,
      monthlyContribution: recommendation?.monthlyContribution,
      answers,
      createdBy: 'employer_self',
      createdById: null,
    });
    await saveClient({
      ...client,
      ibosConfig: {
        ...existingIbos,
        section105: {
          ...existing105,
          answers,
          employerConsultationRequested: true,
          requestedAt: new Date().toISOString(),
          recommendedPlanId: recommendation?.planId,
          monthlyContribution: recommendation?.monthlyContribution,
          recommendedPackageId: recommendation?.primaryPackage?.id,
          alternativePackageIds: (recommendation?.alternativePackages || []).map(p => p.id),
          selectedPackageId: selectedPkgId,
        },
      },
      savedOptions: builtOptions,
    });
    setJustSaved(true);
  };

  if (!client) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: C.textMid }}>
        No client selected.
      </div>
    );
  }

  // ─── Intro Screen ────────────────────────────────────────────────
  if (step === 'intro') {
    return (
      <WizardShell title={copy.title} subtitle={copy.subtitle} onClose={onClose}>
        <div
          data-testid="s105-intro"
          style={{
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: 28,
            maxWidth: 640,
            margin: '0 auto',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              background: C.accentBg,
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="medical-outline" style={{ fontSize: 32, color: C.accent }} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: '0 0 8px' }}>
            {isEmployer
              ? 'Find your ideal healthcare reimbursement plan'
              : 'Identify the right Section 105 structure'}
          </h2>
          <p style={{ fontSize: 14, color: C.textMid, lineHeight: 1.6, margin: '0 0 24px' }}>
            {isEmployer
              ? 'Answer 6 short questions and we\u2019ll recommend the plan that fits your company best \u2014 with a clear view of how it pairs with your HealthCues program.'
              : `Six guided questions map this employer to the best fit among ${planTypes.length} HRA plan types, with eligibility-bridge math and ready-to-generate plan documents.`}
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              type="button"
              onClick={startWizard}
              data-testid="s105-start-btn"
              style={{
                padding: '12px 24px',
                background: C.accent,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {isEmployer ? 'Start — 2 minutes' : 'Start wizard'}
              <Icon name="arrow-forward" style={{ marginLeft: 8, fontSize: 14, verticalAlign: 'middle' }} />
            </button>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '12px 24px',
                  background: 'transparent',
                  color: C.textMid,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {copy.closeLabel}
              </button>
            )}
          </div>
        </div>
      </WizardShell>
    );
  }

  // ─── Wizard Questions ────────────────────────────────────────────
  if (step === 'wizard' && q) {
    const wording = isEmployer ? q.employerWording : q.internalWording;
    const helpText = isEmployer ? q.employerHelp : q.internalHelp;
    const selected = answers[q.id];

    return (
      <WizardShell title={copy.title} subtitle={copy.subtitle} onClose={onClose}>
        {/* Progress indicator */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 20,
            maxWidth: 720,
            margin: '0 auto 20px',
          }}
        >
          {questions.map((_, idx) => (
            <div
              key={idx}
              style={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                background: idx <= currentQ ? C.accent : C.border,
                transition: 'background 200ms ease',
              }}
            />
          ))}
          <span style={{ fontSize: 11, color: C.textMid, fontWeight: 600, marginLeft: 8, whiteSpace: 'nowrap' }}>
            Step {currentQ + 1} of {totalQs}
          </span>
        </div>

        <div
          data-testid={`s105-question-${q.id}`}
          style={{
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: 28,
            maxWidth: 720,
            margin: '0 auto',
          }}
        >
          <h3 style={{ fontSize: 20, fontWeight: 700, color: C.text, margin: '0 0 8px' }}>{wording}</h3>
          {helpText && (
            <p style={{ fontSize: 13, color: C.textMid, lineHeight: 1.5, margin: '0 0 20px' }}>{helpText}</p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {q.options.map((opt) => {
              const isSelected = selected === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => handleAnswer(q.id, opt.id)}
                  data-testid={`s105-option-${q.id}-${opt.id}`}
                  style={{
                    textAlign: 'left',
                    padding: 14,
                    borderRadius: 10,
                    border: `1.5px solid ${isSelected ? C.accent : C.border}`,
                    background: isSelected ? C.accentBg : C.card,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    transition: 'border-color 150ms ease, background 150ms ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.borderColor = C.textMid;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.borderColor = C.border;
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 9,
                      border: `2px solid ${isSelected ? C.accent : C.border}`,
                      background: isSelected ? C.accent : 'transparent',
                      flexShrink: 0,
                      marginTop: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {isSelected && <Icon name="checkmark" style={{ fontSize: 10, color: '#fff' }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 2 }}>
                      {opt.label}
                    </div>
                    {opt.sublabel && (
                      <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.4 }}>{opt.sublabel}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 20,
              paddingTop: 16,
              borderTop: `1px solid ${C.borderLight}`,
            }}
          >
            <button
              type="button"
              disabled={currentQ === 0}
              onClick={() => setCurrentQ(Math.max(0, currentQ - 1))}
              data-testid="s105-back-btn"
              style={{
                padding: '8px 14px',
                background: 'transparent',
                color: currentQ === 0 ? C.textLight : C.textMid,
                border: `1px solid ${currentQ === 0 ? C.borderLight : C.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                cursor: currentQ === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              <Icon name="arrow-back" style={{ fontSize: 12, marginRight: 6, verticalAlign: 'middle' }} />
              Back
            </button>
            <button
              type="button"
              disabled={!selected}
              onClick={() => {
                if (currentQ < totalQs - 1) {
                  setCurrentQ(currentQ + 1);
                } else {
                  setStep('results');
                  persistAnswers(answers);
                }
              }}
              data-testid="s105-next-btn"
              style={{
                padding: '8px 18px',
                background: selected ? C.accent : C.border,
                color: selected ? '#fff' : C.textLight,
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 700,
                cursor: selected ? 'pointer' : 'not-allowed',
              }}
            >
              {currentQ === totalQs - 1 ? 'See recommendation' : 'Next'}
              <Icon name="arrow-forward" style={{ fontSize: 12, marginLeft: 6, verticalAlign: 'middle' }} />
            </button>
          </div>
        </div>
      </WizardShell>
    );
  }

  // ─── Results ─────────────────────────────────────────────────────
  if (step === 'results' && recommendation) {
    return (
      <WizardShell title={copy.title} subtitle={copy.subtitle} onClose={onClose}>
        <PackageResultsView
          mode={mode}
          client={client}
          answers={answers}
          recommendation={recommendation}
          activePackage={activePackage}
          onSelectPackage={setActivePackageId}
          bridge={bridge}
          currentFicaSavings={currentFicaSavings}
          documentTemplates={documentTemplates}
          onRestart={restart}
          onSendToEmployer={isEmployer ? requestConsultation : sendToEmployer}
          onViewDoc={(key) => setViewingDocKey(key)}
          justSaved={justSaved}
          copy={copy}
          stored={stored}
        />
        {viewingDocKey && (
          <DocumentModal
            docKey={viewingDocKey}
            template={documentTemplates[viewingDocKey]}
            client={client}
            answers={answers}
            bridge={bridge}
            onClose={() => setViewingDocKey(null)}
          />
        )}
      </WizardShell>
    );
  }

  return null;
}

// ───────────────────────────────────────────────────────────────────
// Shell: title bar + slot for children
function WizardShell({ title, subtitle, onClose, children }) {
  return (
    <div style={{ background: C.bg, minHeight: '100%', padding: 20 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          maxWidth: 720,
          margin: '0 auto 16px',
        }}
      >
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, margin: 0 }}>{title}</h2>
          <p style={{ fontSize: 13, color: C.textMid, margin: '4px 0 0' }}>{subtitle}</p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="s105-close-btn"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 6,
              color: C.textMid,
            }}
          >
            <Icon name="close" style={{ fontSize: 22 }} />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Package-aware Results view — shows recommended package, alternatives,
// component coverage layers, eligibility bridge, comparison table, and
// package-level deliverables.
function PackageResultsView({
  mode,
  client,
  answers,
  recommendation,
  activePackage,
  onSelectPackage,
  bridge,
  currentFicaSavings,
  documentTemplates,
  onRestart,
  onSendToEmployer,
  onViewDoc,
  justSaved,
  copy,
  stored,
}) {
  const isEmployer = mode === 'employer';
  const { primaryPackage, alternativePackages, monthlyContribution } = recommendation;
  const alreadySent = !!(stored && (stored.presentedToEmployer || stored.employerConsultationRequested));

  if (!primaryPackage) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: C.textMid }}>
        No matching package found for these answers.
      </div>
    );
  }

  const allPackages = [primaryPackage, ...(alternativePackages || [])];
  const isPrimaryActive = activePackage?.id === primaryPackage.id;

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Recommended Package Hero */}
      <PackageHero
        pkg={activePackage || primaryPackage}
        isPrimary={isPrimaryActive}
        isEmployer={isEmployer}
        monthlyContribution={monthlyContribution}
        bridge={bridge}
      />

      {/* Coverage layer & component breakdown */}
      <PackageComponents pkg={activePackage || primaryPackage} isEmployer={isEmployer} />

      {/* Eligibility Bridge */}
      {bridge && bridge.totalEmployees > 0 && (
        <div data-testid="s105-bridge-card" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>
            Eligibility bridge analysis
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: '0 0 12px' }}>
            {isEmployer ? 'How this changes who qualifies' : 'Who moves from ineligible to eligible'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <BridgeStat label={isEmployer ? 'Qualify today' : 'Currently eligible'} value={`${bridge.currentEligible} / ${bridge.totalEmployees}`} sub={`${Math.round(bridge.currentPct * 100)}% of workforce`} tone="neutral" />
            <BridgeStat label={isEmployer ? 'Added by this plan' : `Bridged by $${monthlyContribution}/mo`} value={`+${bridge.bridgedCount}`} sub={bridge.bridgedCount > 0 ? 'Additional employees qualify' : 'No new employees bridged'} tone="green" />
            <BridgeStat label={isEmployer ? 'Will qualify after' : 'Projected eligible'} value={`${bridge.newEligible} / ${bridge.totalEmployees}`} sub={`${Math.round(bridge.newPct * 100)}% of workforce`} tone="accent" />
            <BridgeStat label={isEmployer ? 'Additional FICA savings' : 'Added employer FICA / yr'} value={`$${(bridge.bridgeFicaSavings || 0).toLocaleString()}`} sub="Incremental annual" tone="green" />
          </div>
          {!isEmployer && (
            <div style={{ marginTop: 14, padding: 12, background: C.accentBg, border: `1px solid ${C.accentLight}`, borderRadius: 8, fontSize: 12, color: C.text, lineHeight: 1.6 }}>
              <strong>Internal note:</strong> Current 125 employer FICA ≈{' '}
              <strong>${currentFicaSavings.toLocaleString()}/yr</strong>. With this package at $
              {monthlyContribution}/mo, we add{' '}
              <strong>${(bridge.bridgeFicaSavings || 0).toLocaleString()}/yr</strong> through the eligibility bridge.
              Employer annual HRA cost ≈ <strong>${(bridge.employerAnnualCost || 0).toLocaleString()}</strong>, net ≈{' '}
              <strong>${(bridge.netAnnualCost || 0).toLocaleString()}</strong>.
            </div>
          )}
        </div>
      )}

      {/* Alternative Packages */}
      {alternativePackages && alternativePackages.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
            {isEmployer ? 'Other options available' : 'Also consider'}
          </div>
          <div data-testid="s105-alternatives-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {alternativePackages.map((pkg) => (
              <AlternativePackageCard
                key={pkg.id}
                pkg={pkg}
                isEmployer={isEmployer}
                isActive={activePackage?.id === pkg.id}
                onSelect={() => onSelectPackage(pkg.id)}
              />
            ))}
            {!isPrimaryActive && (
              <div style={{ border: `1px dashed ${C.border}`, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', gap: 6, background: C.bg }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Currently viewing {activePackage?.name}
                </div>
                <button
                  type="button"
                  onClick={() => onSelectPackage(primaryPackage.id)}
                  data-testid="s105-back-to-primary-btn"
                  style={{ padding: '8px 12px', background: C.accent, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                >
                  ← Back to recommended ({primaryPackage.name})
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comparison Table */}
      <ComparisonTable packages={allPackages} primaryId={primaryPackage.id} bridge={bridge} isEmployer={isEmployer} />

      {/* Deliverables */}
      <PackageDeliverables pkg={activePackage || primaryPackage} documentTemplates={documentTemplates} onViewDoc={onViewDoc} isEmployer={isEmployer} />

      {/* Action bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.textMid }}>
          {alreadySent ? (
            <>
              <Icon name="checkmark-circle" style={{ fontSize: 16, color: C.green }} />
              {isEmployer
                ? 'Consultation requested — your HealthCues team will reach out.'
                : `Recommendation sent to Employer Portal${stored && stored.selectedPackageId ? ` (${stored.selectedPackageId})` : ''}.`}
            </>
          ) : (
            <>Your answers are saved on this client record.</>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={onRestart}
            data-testid="s105-restart-btn"
            style={{ padding: '10px 16px', background: 'transparent', color: C.textMid, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            {copy.restartLabel}
          </button>
          <button
            type="button"
            onClick={onSendToEmployer}
            data-testid="s105-send-btn"
            style={{ padding: '10px 18px', background: C.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            <Icon name="paper-plane-outline" style={{ fontSize: 12, marginRight: 6, verticalAlign: 'middle' }} />
            {copy.sendLabel}
          </button>
        </div>
      </div>
      {justSaved && (
        <div data-testid="s105-saved-toast" style={{ position: 'fixed', bottom: 24, right: 24, padding: '10px 16px', background: C.green, color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000 }}>
          <Icon name="checkmark" style={{ marginRight: 6, fontSize: 13, verticalAlign: 'middle' }} />
          Saved
        </div>
      )}
    </div>
  );
}

// ─── Hero card ────────────────────────────────────────────────────
function PackageHero({ pkg, isPrimary, isEmployer, monthlyContribution, bridge }) {
  const headline = isEmployer ? pkg.headlineEmployer : pkg.headlineInternal;
  const why = isEmployer ? pkg.whyThisPackageEmployer : pkg.whyThisPackage;
  return (
    <div data-testid="s105-recommendation-card" style={{ background: 'linear-gradient(135deg, #0066cc 0%, #2563eb 100%)', borderRadius: 12, padding: 24, color: '#fff', position: 'relative' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        {isPrimary ? '⭐ Recommended package' : 'Alternative package'}
        {pkg.flagged && (
          <span style={{ padding: '2px 8px', borderRadius: 999, background: 'rgba(255, 174, 0, 0.25)', color: '#ffd166', fontSize: 10, fontWeight: 800, letterSpacing: '0.05em' }}>
            ⚠ Legal review required
          </span>
        )}
      </div>
      <h2 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 4px' }}>{pkg.name}</h2>
      <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 12 }}>{headline}</div>
      <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0, opacity: 0.95 }}>{why}</p>
      <div style={{ marginTop: 16, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
        <HeroStat label={isEmployer ? 'Estimated cost' : 'Est. employer cost'} value={`$${pkg.estMonthlyEmployerCost}/ee/mo`} />
        <HeroStat label="Setup time" value={pkg.setupWeeks || '—'} />
        <HeroStat label="Legal review" value={pkg.legalReview || 'Standard'} />
        {bridge && bridge.bridgedCount > 0 && (
          <HeroStat label="Eligibility bridge" value={`+${bridge.bridgedCount} employees`} />
        )}
        <HeroStat label="Per-employee monthly" value={`$${monthlyContribution}/mo`} />
      </div>
    </div>
  );
}

// ─── Component breakdown ───────────────────────────────────────────
function PackageComponents({ pkg, isEmployer }) {
  const components = pkg.components || [];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
      {components.map((c, idx) => (
        <ComponentCard key={`${c.layer}-${idx}`} comp={c} isEmployer={isEmployer} />
      ))}
      {pkg._coverage && pkg._coverage.id === 'health_share' && (
        <HealthShareDisclaimerCard coverage={pkg._coverage} pkg={pkg} />
      )}
      {pkg._coverage && pkg._coverage.id === 'mec' && (
        <MECRationaleCard coverage={pkg._coverage} pkg={pkg} isEmployer={isEmployer} />
      )}
    </div>
  );
}

function ComponentCard({ comp, isEmployer }) {
  const statusTone = {
    active: { bg: C.greenLight, color: C.green, label: 'Active' },
    recommended: { bg: C.accentBg, color: C.accent, label: 'Recommended' },
    required: { bg: C.greenLight, color: C.green, label: 'Required' },
    alternative: { bg: '#fff3e0', color: C.orange, label: 'Alternative' },
    in_place: { bg: C.bg, color: C.textMid, label: 'In place' },
  }[comp.status] || { bg: C.bg, color: C.textMid, label: comp.status };
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: statusTone.bg, color: statusTone.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {statusTone.label}
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>{comp.layer}</div>
      <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.5, marginBottom: 8 }}>{comp.role}</div>
      <div style={{ fontSize: 11, color: C.textLight, borderTop: `1px solid ${C.borderLight}`, paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div><strong>{isEmployer ? 'Cost' : 'Employer cost'}:</strong> {comp.costToEmployer}</div>
        <div><strong>Employee:</strong> {comp.employeeImpact}</div>
      </div>
    </div>
  );
}

function MECRationaleCard({ coverage, pkg, isEmployer }) {
  const role = coverage.rolesByPackage?.[pkg.id] || coverage.employerSummary;
  return (
    <div style={{ background: C.greenLight, border: `1px solid ${C.greenBorder || C.green}`, borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icon name="shield-checkmark-outline" style={{ fontSize: 14, color: C.green }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: C.green, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Why MEC is included
        </span>
      </div>
      <div style={{ fontSize: 12, color: C.text, lineHeight: 1.6, marginBottom: 10 }}>{role}</div>
      <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.6 }}>
        <strong>{isEmployer ? 'What employees get' : 'Coverage benefits'}:</strong>
        <ul style={{ paddingLeft: 18, margin: '4px 0 0' }}>
          {(coverage.benefits || []).map((b, i) => (<li key={i}>{b}</li>))}
        </ul>
      </div>
      <div style={{ fontSize: 11, color: C.textLight, marginTop: 8 }}>
        Estimated cost: <strong style={{ color: C.text }}>${coverage.monthlyCostRange?.[0] || coverage.monthlyCostEstimate}-${coverage.monthlyCostRange?.[1] || coverage.monthlyCostEstimate}/employee/month</strong>
      </div>
    </div>
  );
}

function HealthShareDisclaimerCard({ coverage, pkg }) {
  const role = coverage.rolesByPackage?.[pkg.id];
  return (
    <div data-testid="health-share-disclaimer" style={{ background: '#fff3e0', border: `1.5px solid ${C.orange}`, borderRadius: 12, padding: 16, gridColumn: '1 / -1' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Icon name="warning-outline" style={{ fontSize: 18, color: C.orange }} />
        <span style={{ fontSize: 12, fontWeight: 800, color: C.orange, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Important compliance notes — Health Share
        </span>
      </div>
      {role && <div style={{ fontSize: 12, color: C.text, lineHeight: 1.6, marginBottom: 10 }}>{role}</div>}
      <ul style={{ paddingLeft: 18, margin: 0, fontSize: 12, color: C.text, lineHeight: 1.7 }}>
        {(coverage.requiredDisclaimers || []).map((d, i) => (<li key={i}>{d}</li>))}
      </ul>
      <div style={{ marginTop: 10, fontSize: 11, color: C.orange, fontWeight: 700 }}>
        This option requires specialized legal guidance. HealthCues recommends consulting benefits counsel before proceeding.
      </div>
    </div>
  );
}

// ─── Alternative package card ─────────────────────────────────────
function AlternativePackageCard({ pkg, isEmployer, isActive, onSelect }) {
  const headline = isEmployer ? pkg.headlineEmployer : pkg.headlineInternal;
  return (
    <div data-testid={`s105-alt-package-${pkg.id}`} style={{ background: C.card, border: `${isActive ? '2px' : '1px'} solid ${isActive ? C.accent : C.border}`, borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{pkg.name}</span>
        {pkg.flagged && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#fff3e0', color: C.orange }}>⚠ Review</span>
        )}
      </div>
      <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.5 }}>{headline}</div>
      <div style={{ fontSize: 11, color: C.textLight, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div><strong>{isEmployer ? 'Estimated cost' : 'Est. cost'}:</strong> ${pkg.estMonthlyEmployerCost}/ee/mo</div>
        <div><strong>Setup:</strong> {pkg.setupWeeks}</div>
        <div><strong>Best for:</strong> {isEmployer ? pkg.bestForEmployer : pkg.bestFor}</div>
      </div>
      <button
        type="button"
        onClick={onSelect}
        data-testid={`s105-select-alt-${pkg.id}`}
        style={{ alignSelf: 'flex-start', marginTop: 6, padding: '7px 12px', background: isActive ? C.green : C.accent, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
      >
        {isActive ? '✓ Selected' : isEmployer ? 'Learn more' : 'View details'}
      </button>
    </div>
  );
}

// ─── Comparison Table ─────────────────────────────────────────────
function ComparisonTable({ packages, primaryId, bridge, isEmployer }) {
  if (!packages || packages.length < 2) return null;
  const internalRows = [
    { label: '105 plan type', get: (p) => (p._planType?.type || p.section105Type || '—') },
    { label: 'Coverage layer', get: (p) => (p._coverage?.name || (p.coverageLayer === null ? 'Group health (existing)' : 'None')) },
    { label: 'Employer cost / mo', get: (p) => `~$${p.estMonthlyEmployerCost}/ee` },
    { label: 'Range', get: (p) => p.employerCostRange ? `$${p.employerCostRange[0]}–$${p.employerCostRange[1]}` : '—' },
    { label: 'Setup time', get: (p) => p.setupWeeks || '—' },
    { label: 'Legal review', get: (p) => p.legalReview || 'Standard' },
    { label: 'Eligibility bridge', get: () => (bridge && bridge.bridgedCount > 0 ? `+${bridge.bridgedCount} employees` : '—') },
    { label: 'Best for', get: (p) => p.bestFor || '—' },
  ];
  const employerRows = [
    { label: 'Monthly cost', get: (p) => `~$${p.estMonthlyEmployerCost}/employee` },
    { label: 'After tax deduct.', get: (p) => `~$${Math.round(p.estMonthlyEmployerCost * 0.73)}/employee` },
    { label: 'Employees added', get: () => (bridge && bridge.bridgedCount > 0 ? `+${bridge.bridgedCount} qualify` : '—') },
    { label: 'Preventive care', get: (p) => p._coverage ? '✓ Included' : 'Add separately' },
    { label: 'Simplicity', get: (p) => (p.legalReview === 'Required' ? 'Moderate' : 'Simple') },
    { label: 'Time to start', get: (p) => p.setupWeeks || '—' },
    { label: 'Best for', get: (p) => p.bestForEmployer || '—' },
  ];
  const rows = isEmployer ? employerRows : internalRows;
  return (
    <div data-testid="s105-comparison-table" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, background: C.bg }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Side-by-side comparison
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: C.bg }}>
              <th style={{ ...thStyle, textAlign: 'left' }}></th>
              {packages.map((p) => (
                <th key={p.id} style={{ ...thStyle, background: p.id === primaryId ? C.accentBg : C.bg, color: p.id === primaryId ? C.accent : C.text, minWidth: 140 }}>
                  {p.id === primaryId && <span style={{ marginRight: 4 }}>⭐</span>}
                  {isEmployer && p.id === primaryId ? 'Recommended' : p.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ridx) => (
              <tr key={row.label} style={{ background: ridx % 2 === 0 ? C.card : C.bg }}>
                <td style={{ ...tdStyle, fontWeight: 700, color: C.textMid, width: 200 }}>{row.label}</td>
                {packages.map((p) => (
                  <td key={p.id} style={{ ...tdStyle, background: p.id === primaryId ? C.accentBg : 'transparent', color: C.text }}>
                    {row.get(p)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
const thStyle = { padding: '12px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.textLight, borderBottom: `1px solid ${C.border}` };
const tdStyle = { padding: '10px 14px', borderBottom: `1px solid ${C.borderLight}`, verticalAlign: 'top' };

// ─── Package deliverables ─────────────────────────────────────────
function PackageDeliverables({ pkg, documentTemplates, onViewDoc, isEmployer }) {
  const deliverables = getPackageDeliverables(pkg);
  const groups = deliverables.reduce((acc, d) => {
    const g = d.group || 'Plan';
    if (!acc[g]) acc[g] = [];
    acc[g].push(d);
    return acc;
  }, {});
  return (
    <div data-testid="s105-deliverables" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
        {isEmployer ? 'Plan documents you\u2019ll receive' : 'Package deliverables'}
      </div>
      {Object.entries(groups).map(([group, items]) => (
        <div key={group} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            {group}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8 }}>
            {items.map((d) => {
              const hasTemplate = !!documentTemplates[d.id];
              return (
                <div key={d.id} data-testid={`s105-deliverable-${d.id}`} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, background: C.bg, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="document-outline" style={{ fontSize: 12, color: C.accent }} />
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{d.title}</div>
                  </div>
                  <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.4 }}>{d.description}</div>
                  {hasTemplate && (
                    <button type="button" onClick={() => onViewDoc(d.id)} data-testid={`s105-view-doc-${d.id}`} style={{ alignSelf: 'flex-start', marginTop: 2, padding: '3px 8px', fontSize: 10, fontWeight: 700, color: C.accent, background: C.accentBg, border: `1px solid ${C.accentLight}`, borderRadius: 4, cursor: 'pointer' }}>
                      View sample
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────
// (HeroStat / BridgeStat / InfoCard / SpecRow are shared by PackageResultsView)
function HeroStat({ label, value }) {
  return (
    <div style={{ minWidth: 100 }}>
      <div style={{ fontSize: 10, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function BridgeStat({ label, value, sub, tone = 'neutral' }) {
  const toneColor = tone === 'green' ? C.green : tone === 'accent' ? C.accent : C.text;
  const bg = tone === 'green' ? C.greenLight : tone === 'accent' ? C.accentBg : C.bg;
  return (
    <div style={{ padding: 12, borderRadius: 8, background: bg, border: `1px solid ${C.borderLight}` }}>
      <div style={{ fontSize: 10, color: C.textMid, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: toneColor, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function SpecRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderBottom: `1px solid ${C.borderLight}`, fontSize: 12 }}>
      <div style={{ color: C.textMid, fontWeight: 600, flexShrink: 0 }}>{label}</div>
      <div style={{ color: C.text, textAlign: 'right' }}>{value}</div>
    </div>
  );
}

// ─── Document Modal (client-side markdown renderer) ───────────────
function DocumentModal({ docKey, template, client, answers, bridge, onClose }) {
  const merge = buildMergeFields(client, answers, bridge);
  const rendered = renderDocumentTemplate(template || '', merge);

  return (
    <div
      data-testid="s105-doc-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 720,
          maxHeight: '85vh',
          background: C.card,
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: `1px solid ${C.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: C.bg,
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Sample document
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{docKey}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="s105-doc-close-btn"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.textMid, padding: 4 }}
          >
            <Icon name="close" style={{ fontSize: 22 }} />
          </button>
        </div>
        <div
          style={{
            padding: 24,
            overflowY: 'auto',
            flex: 1,
            fontFamily: "'Source Code Pro', 'JetBrains Mono', monospace",
            fontSize: 13,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            color: C.text,
            background: C.card,
          }}
        >
          {rendered || <span style={{ color: C.textLight, fontStyle: 'italic' }}>No template content configured.</span>}
        </div>
        <div
          style={{
            padding: '12px 18px',
            borderTop: `1px solid ${C.border}`,
            fontSize: 11,
            color: C.textLight,
            background: C.bg,
          }}
        >
          Rendered client-side from an admin-editable markdown template. Merge fields use the current client\u2019s company and wizard answers.
        </div>
      </div>
    </div>
  );
}
